import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import { CellInputRefs } from 'hooks/useKeyboardNavigation';
import { useTranslation } from 'react-i18next';
import { ImageExtension, ReturnOrderLine } from 'returnCenter-api';
import { styled } from 'styled-components';
import getImage, { ImageType } from 'utils/getImages';

type ProductInfoCellProps = {
    item: ReturnOrderLine;
    orderNumber: string;
    rowRefs?: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
    itemId?: number | string;
    returnImages?: { [key: string]: string };
    returnSlipImages?: { [key: string]: string };
    rowIndex?: string | number;
    colIndex?: string | number;
    cellInputRefs?: CellInputRefs;
    handleArrowKeyDown?: (e: any, orderId: any, rowIndex: any, colIndex: any) => void;
    cellRefPrefix?: string;
};

const ProductInfoCell = ({
    item,
    orderNumber,
    rowRefs,
    itemId,
    rowIndex,
    colIndex,
    cellInputRefs,
    handleArrowKeyDown,
    cellRefPrefix = '',
}: ProductInfoCellProps) => {
    const { t } = useTranslation();
    return (
        <Container
            className="product-details-container"
            key={itemId}
            gap="8px"
            tabIndex={cellInputRefs ? 0 : undefined}
            role="rowcell"
            data-row={rowIndex}
            data-row-cell={colIndex}
            data-item-id={itemId}
            ref={(el) => {
                const key = `${cellRefPrefix}${rowIndex}-${colIndex}`;
                if (cellInputRefs) cellInputRefs.current[key] = el;
            }}
            onKeyDown={handleArrowKeyDown ? (e) => handleArrowKeyDown(e, itemId, rowIndex, colIndex) : undefined}
        >
            <ImageWrapper className="product-image-wrapper">
                <img src={getImage(item.image, ImageType.ThumbNail, ImageExtension.Jpg)} alt="Product Thumbnail" />
            </ImageWrapper>
            <PropertyWrapper
                className="product-property-wrapper"
                gap="4px"
                column
                justify="center"
                $grow={1}
                ref={(el) => {
                    if (rowRefs) {
                        // Composite keys prevent same-EAN/SKU rows across different orders from overwriting each other
                        if (el && item.ean) rowRefs.current[`${orderNumber}-${item.ean}`] = el;
                        if (el && item.sku) rowRefs.current[`${orderNumber}-${item.sku}`] = el;
                    }
                }}
            >
                <Text as="label" xBold fontSize={11}>
                    {t('SKU')}: {item.sku ?? 'Missing Sku'}
                </Text>
                <Text bold as="label" fontSize={11}>
                    {item.orderProductName || item.productName || 'Missing product name'}
                </Text>
                <Text xBold as="label" fontSize={11}>
                    {t('EAN')}: {item.ean ?? 'Missing EAN'}
                </Text>
            </PropertyWrapper>
        </Container>
    );
};

export default ProductInfoCell;

const Container = styled(Flex)`
    overflow-wrap: anywhere;
`;

const ImageWrapper = styled.div`
    width: 40px;
    height: 57px;

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
`;

const PropertyWrapper = styled(Flex)`
    flex: 1;
    margin-bottom: 0.5rem;
    &.highlight-row {
        background-color: #fff9c4 !important;
        transition: background 0.5s;
    }
`;
